import { GrandstandButton } from '@/components/Buttons'
import { ButtonLabel, HeadingLabel, SubheadingLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { LogoLeagueObjV2 } from '@grandstand/presentation-models'
import Link from 'next/link'
import { PropsWithChildren, useState } from 'react'
import styled from 'styled-components'
import { LoadingSpinner } from './loading'

interface ModalProps {
  header?: string | JSX.Element
  title: string | JSX.Element
  msg: string | JSX.Element
  canDismiss?: boolean
  onDismiss?: () => void
  buttons: {
    label: string
    onClick: () => void
  }[]
  footerLink?: {
    label: string
    onClick?: () => void
    href?: string
  }
  showLoading?: boolean
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 528px;
  max-height: 100%;
  overflow-y: scroll;
  @media (max-width: 768px) {
    width: 343px;
    padding: 24px 20px;
  }
  background-color: var(--surface);
  padding: 32px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  z-index: 10000;

  /* While we want to allow this to scroll, we need to hide the scrollbars otherwise you get ugly padding on the right */
  & {
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-y: scroll;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const ModalTitle = styled(HeadingLabel)`
  margin-top: 16px;
  margin-bottom: 10px;
  color: var(--on-background);
  text-align: center;
`

const ModalMessage = styled(SubheadingLabel)`
  color: var(--on-background, #000);
  text-align: center;
  margin-bottom: 16px;
`

const ModalButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  gap: 8px;
  @media (max-width: 768px) {
    margin: 0;
  }
  & > button {
    width: 100%;
  }
  width: 100%;
`

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`

const ModalLogosContainer = styled.div`
  display: flex;
  justify-content: center;
`

const LogoImg = styled.img`
  height: 52px;
  width: 52px;
`

const RemainingLogoCount = styled.div`
  display: flex;
  height: 52px;
  width: 52px;
  border-radius: 50%;
  background-color: var(--canvas-light);
  justify-content: center;
  align-items: center;
`

const RemainingCountText = styled(HeadingLabel)`
  color: var(--on-light);
`

const LinkText = styled(ButtonLabel)`
  color: var(--on-background-alt);
  text-decoration: underline;
  font-weight: normal;
  padding-top: 12px;
  padding-bottom: 12px;
  &:hover {
    cursor: pointer;
  }
`

export const ModalLogos = ({ logos }: { logos?: LogoLeagueObjV2[] }) => {
  if (logos) {
    const logosToRender: LogoLeagueObjV2[] = []
    // show only first 4 and make the 5th one a number
    for (let i = 0; i < logos.length; i++) {
      if (i < 4) {
        logosToRender.push(logos[i])
      } else {
        logosToRender.push({ dark_logo: '', light_logo: '', name: `${logos.length - 4}`, team: '' })
        break
      }
    }
    return (
      <ModalLogosContainer>
        {logos && logos.length <= logosToRender.length
          ? logosToRender.map((logo) => {
              const logoImg = logo.light_logo
              return <LogoImg key={logoImg} src={logoImg} />
            })
          : logosToRender.map((logo) => {
              const logoImg = logo.light_logo
              if (logoImg !== '') {
                return <LogoImg key={logoImg} src={logoImg} />
              } else {
                return (
                  <RemainingLogoCount key="5">
                    <RemainingCountText fontSize="2">{`+${logo.name}`}</RemainingCountText>
                  </RemainingLogoCount>
                )
              }
            })}
      </ModalLogosContainer>
    )
  }
  return null
}

const Modal = ({
  header,
  title,
  msg,
  buttons,
  canDismiss = true,
  onDismiss = () => {},
  footerLink,
  showLoading,
  children,
}: PropsWithChildren<ModalProps>) => {
  const [showModal, setShowModal] = useState(true)

  const handleCloseModal = () => {
    if (canDismiss) {
      setShowModal(false)
      onDismiss()
    }
  }

  if (!showModal) {
    return null
  }

  return (
    <ModalOverlay>
      <ModalContent data-theme="light">
        {canDismiss && (
          <CloseButton onClick={handleCloseModal}>
            <GrandstandIcon icon="close" />
          </CloseButton>
        )}
        {typeof header === 'string' ? <ModalTitle fontSize="3">{header}</ModalTitle> : header}
        {typeof title === 'string' ? <ModalTitle fontSize="2">{title}</ModalTitle> : title}
        {typeof msg === 'string' ? <ModalMessage fontSize="1">{msg}</ModalMessage> : msg}
        {children}
        {showLoading ? (
          <ModalButtonContainer>
            <LoadingSpinner size="60px" />
          </ModalButtonContainer>
        ) : (
          <ModalButtonContainer>
            {buttons.map((button, index) => (
              <GrandstandButton
                key={index}
                onClick={button.onClick}
                buttonSize="md"
                buttonStyle={index === 1 ? 'alt' : 'primary'}
              >
                {button.label}
              </GrandstandButton>
            ))}
            {footerLink &&
              (footerLink.href ? (
                <Link href={footerLink.href}>
                  <LinkText fontSize="sm">{footerLink.label}</LinkText>
                </Link>
              ) : (
                <LinkText fontSize="sm" onClick={footerLink.onClick}>
                  {footerLink.label}
                </LinkText>
              ))}
          </ModalButtonContainer>
        )}
      </ModalContent>
    </ModalOverlay>
  )
}

export default Modal
